import {getTestProps} from '@benchling/js-test-utils';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';

import {assert} from 'Asserts';
import {CalendarView} from 'calendar/CalendarConstants';
import * as CalendarConstants from 'calendar/CalendarConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Spinner from 'ui-library/Spinner';

import CalendarActions from './CalendarActions';
import CalendarUtil from './CalendarUtil';

const CALENDAR_VIEW_TO_DISPLAY: {[view in CalendarView]: string} = {
  [CalendarView.DAY_VIEW]: 'Day',
  [CalendarView.WEEK_VIEW]: '2 Weeks',
  [CalendarView.MONTH_VIEW]: 'Month',
};

interface CurrentViewSelectorProps {
  currentView: CalendarView;
}

class CurrentViewSelector extends React.Component<CurrentViewSelectorProps> {
  _handleViewChange = (view: CalendarView): void => {
    CalendarActions.changeView(view);
  };

  override render(): JSX.Element {
    return (
      <div
        className="currentViewSelector-container buttonGroup"
        {...getTestProps({
          component: 'CalendarToolbar',
          element: 'currentViewSelector',
          key: this.props.currentView,
        })}
      >
        {[CalendarView.DAY_VIEW, CalendarView.WEEK_VIEW, CalendarView.MONTH_VIEW].map(
          (view: CalendarView) => (
            <Button
              key={view}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              className={classNames({'is-active': this.props.currentView === view})}
              onClick={() => {
                this._handleViewChange(view);
              }}
              size={ButtonSize.SMALL}
              text={CALENDAR_VIEW_TO_DISPLAY[view]}
            />
          )
        )}
      </div>
    );
  }
}

interface CalendarToolbarProps {
  currentView: CalendarView;
  isLoadingData: boolean;
  today: moment.Moment;
  viewLabel: string;
  viewRange: Array<moment.Moment>;
  visibleDate: moment.Moment;
}

class CalendarToolbar extends React.Component<CalendarToolbarProps> {
  _navigateUnit = (): moment.unitOfTime.DurationConstructor => {
    switch (this.props.currentView) {
      case CalendarConstants.DAY_VIEW:
        return 'days';
      case CalendarConstants.WEEK_VIEW:
        return 'weeks';
      default:
        assert(
          this.props.currentView === CalendarConstants.MONTH_VIEW,
          `Unexpected view: ${this.props.currentView}`
        );
        return 'months';
    }
  };

  navigatePreviousMonth = (): void => {
    CalendarActions.navigateDate(this.props.visibleDate.clone().subtract(1, this._navigateUnit()));
  };

  navigateNextMonth = (): void => {
    CalendarActions.navigateDate(this.props.visibleDate.clone().add(1, this._navigateUnit()));
  };

  navigateToday = (): void => {
    CalendarActions.navigateDate(moment());
  };

  override render(): JSX.Element {
    const [viewStart, viewEnd] = this.props.viewRange;
    const enableTodayButton = !CalendarUtil.isDateBetween(this.props.today, viewStart, viewEnd);

    return (
      <div className="u-lr-split">
        <div className="u-vertical-center-items">
          <div className="buttonGroup calendarToolbar-pagination u-mini-spacing-right">
            <Button
              aria-label="Previous month"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              icon="chevron-left"
              onClick={this.navigatePreviousMonth}
              size={ButtonSize.SMALL}
            />
            <Button
              aria-label="Next month"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              icon="chevron"
              onClick={this.navigateNextMonth}
              size={ButtonSize.SMALL}
            />
          </div>
          <Button
            aria-label="Today"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-small-spacing-right"
            disabled={!enableTodayButton}
            onClick={this.navigateToday}
            size={ButtonSize.SMALL}
            text="Today"
          />
          <div className="legacy-font-proxima-17 u-mini-spacing-right">{this.props.viewLabel}</div>
          {this.props.isLoadingData && <Spinner light={true} />}
        </div>
        <CurrentViewSelector currentView={this.props.currentView} />
      </div>
    );
  }
}

export default CalendarToolbar;
